import React from 'react';
import { Polygon } from 'ol/geom';
import { getDistance } from 'ol/sphere';
import cloneDeep from 'lodash.clonedeep';
import { Coordinate } from 'ol/coordinate';
import { fromLonLat, toLonLat } from 'ol/proj';

import {
  GetAreasByZoneId,
  GetAreasWorldMap,
  GetTaskById as GetTaskAssetById,
  GetTasksByIds,
  GetTasksByZoneId
} from '../utils/game-document/assets';
import { Task } from '../types/entities/task';
import { GameContext } from '../contexts/game';
import { useGameEvent } from './use-game-event';
import { PlayerContext } from '../contexts/player';
import { TeamContextState } from '../contexts/team';
import { OpenTask } from '../utils/game-engine/player';
import { MapContext } from '../components/map/map-context';
import { useGameTaskPosition } from './use-game-task-position';
import { GameDocumentContext } from '../contexts/game-document';
import { moveTaskToTop } from '../utils/game-engine/task-position';
import { UpdatePlayerStateAsync } from '../utils/game-engine/base';
import { GetResourceValue } from '../utils/game-document/resources';
import { GetTaskById, getTaskImage } from '../utils/game-engine/assets';
import { AreaEntity, TaskEntity } from '../types/game-document/entities';
import { MapIllustrationContext } from '../components/map/map-illustration-context';
import { isContainPremessageOrIntro } from '../utils/game-document/assets/task-contents';

export const useGameTask = ({
  team,
  mapType,
  accuracy,
  isFacilitator,
  selectedFacilitatorMap
}: {
  mapType: 'openStreetMap' | 'satelliteMap' | 'illustrated' | 'googleMaps';
  selectedFacilitatorMap?: string;
  accuracy?: number;
  isFacilitator?: boolean;
  team?: TeamContextState;
}) => {
  const map = React.useContext(MapContext);
  const mapIllustration = React.useContext(MapIllustrationContext);
  const [game] = React.useContext(GameContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [player, setPlayer] = React.useContext(PlayerContext);

  const callbackCLickRef = React.useRef<
    ((event: MouseEvent | Event) => void) | null
  >(null);

  const {
    addTaskOverlay,
    addTaskDistance,
    getLineDistance,
    removeTaskOverlay,
    updateTaskOverlay,
    removeTaskDistance
  } = useGameTaskPosition({ mapType });

  const { executeTaskEvent } = useGameEvent();

  const mapContext = React.useMemo(
    () => (mapType === 'illustrated' ? mapIllustration : map),
    [mapType, map, mapIllustration]
  );

  const inGameSetting = React.useMemo(
    () =>
      game.gameState?.runtime?.inGame ??
      gameDocument.gameDocument?.settings.inGame,
    [
      game.gameState?.runtime?.inGame,
      gameDocument.gameDocument?.settings.inGame
    ]
  );

  const getProximitySettings = React.useCallback(
    (taskId: string) => {
      const selectedTask = GetTaskAssetById(gameDocument.gameDocument, taskId);
      let tasksRequireProximity = inGameSetting?.tasksRequireProximity;
      let enablePremessageOutsideRange =
        inGameSetting?.enablePremessageOutsideRange;
      let setTaskProximity = parseInt(inGameSetting?.setTaskProximity ?? '0');

      if (selectedTask && selectedTask.settingsOverride) {
        tasksRequireProximity = true;
        enablePremessageOutsideRange =
          selectedTask.settingsOverride.enablePremessageOutsideRange;
        setTaskProximity = selectedTask.settingsOverride.setTaskProximity ?? 0;
      }

      return {
        tasksRequireProximity,
        setTaskProximity,
        enablePremessageOutsideRange
      };
    },
    [
      inGameSetting?.tasksRequireProximity,
      inGameSetting?.enablePremessageOutsideRange,
      inGameSetting?.setTaskProximity
    ]
  );

  const isTaskAccessible = React.useCallback(
    (taskId: string) => {
      const proximitySettings = getProximitySettings(taskId);
      if (!proximitySettings.tasksRequireProximity || mapType === 'illustrated')
        return true;

      let taskPosition = mapContext.getOverlayById(taskId)?.getPosition() || [
        0, 0
      ];
      if (taskPosition) {
        taskPosition = toLonLat(taskPosition);
      }
      return checkIfTaskAccesible(taskPosition, taskId);
    },
    [
      mapType,
      mapContext,
      gameDocument,
      player.playerState,
      getProximitySettings
    ]
  );

  const checkIfTaskAccesible = React.useCallback(
    (position: Coordinate | [number, number], taskId: string) => {
      if (mapType === 'illustrated') return true;

      const gameDoc = gameDocument.gameDocument;
      let isAccesibleTaskOnArea = false;
      const proximitySettings = getProximitySettings(taskId);

      // implement can open task on area with disable proximity setting override
      let areas: AreaEntity[] = [];

      if (player.playerState?.location === 'world-map') {
        areas = GetAreasWorldMap(gameDoc);
      } else {
        areas = GetAreasByZoneId(gameDoc, player.playerState?.location || '');
      }

      areas.forEach((area) => {
        const polygon = new Polygon(area.boundary.geometry.coordinates);
        const taskPoint = fromLonLat(position);
        if (polygon.intersectsCoordinate(taskPoint)) {
          isAccesibleTaskOnArea = !area.disableProximity;
          return;
        }
      });
      // end of implement can open task on area with disable proximity setting override

      const playerLonLat: Coordinate = [
        player.playerState?.coordinates?.longitude || 0,
        player.playerState?.coordinates?.latitude || 0
      ];

      const distance = getDistance(position, playerLonLat);

      // maxAccuracy tolerance set to 60 meters
      const maxAccuracy: number = 60;

      return (
        Math.floor(distance) <= proximitySettings.setTaskProximity ||
        (accuracy! <= maxAccuracy && distance <= accuracy!) ||
        isAccesibleTaskOnArea
      );
    },
    [
      mapType,
      accuracy,
      gameDocument.gameDocument,
      player.playerState?.location,
      player.playerState?.coordinates?.latitude,
      player.playerState?.coordinates?.longitude,
      getProximitySettings
    ]
  );

  const getMapTasks = () => {
    const gameDoc = gameDocument.gameDocument;
    if (gameDoc) {
      if (
        (selectedFacilitatorMap && selectedFacilitatorMap === 'world-map') ||
        player?.playerState?.location === 'world-map'
      ) {
        return GetTasksByIds(
          gameDoc,
          gameDoc?.rules?.worldMap?.tasks?.map((task) => task?.taskAssId)
        );
      } else {
        if (
          (selectedFacilitatorMap && selectedFacilitatorMap !== '') ||
          player?.playerState?.location !== ''
        ) {
          return GetTasksByZoneId(
            selectedFacilitatorMap && selectedFacilitatorMap !== ''
              ? selectedFacilitatorMap!
              : player?.playerState?.location!,
            gameDoc
          );
        }
      }
    }
  };

  const changeTaskIconToComplete = (taskId: string) => {
    const tasks = getMapTasks();
    if (tasks) {
      const task = tasks.find((task) => task.id === taskId);
      if (task) {
        const imgUrl = getTaskImage(
          gameDocument.gameDocument!,
          'completed',
          task,
          player?.playerState?.language?.name
        );
        updateTaskOverlay({ id: taskId, icon: imgUrl });
      }
    }
  };

  const changeTaskIconToInComplete = (taskId: string) => {
    const tasks = getMapTasks();
    if (tasks) {
      const task = tasks.find((task) => task.id === taskId);
      if (task) {
        const imgUrl = getTaskImage(
          gameDocument.gameDocument!,
          'incompleted',
          task,
          player?.playerState?.language?.name
        );
        updateTaskOverlay({ id: taskId, icon: imgUrl });
      }
    }
  };

  const isVisibleMapTask = (mapTask: TaskEntity, playerTask: Task) => {
    let mapTasks: TaskEntity[] = getMapTasks() ?? [];

    //If tasks not belongs to the map, hide the task!
    if (!mapTasks?.find((x) => x?.id === mapTask?.id!)) {
      return false;
    }

    if (playerTask) {
      if (playerTask.status === 'completed' && mapTask.hideWhenComplete) {
        return false;
      }
      return playerTask.isVisible!;
    } else {
      return mapTask?.isVisible!;
    }
  };

  const removeUnusedTaskOverlay = (currentTaskIds: string[]) => {
    const taskContainers =
      document.querySelectorAll<HTMLDivElement>('.task-container');
    const ids: string[] = [];
    taskContainers.forEach((container) => {
      const id = container.id.replaceAll('taskContainer-', '');
      ids.push(id);
    });

    ids.forEach((id) => {
      if (!currentTaskIds.includes(id)) {
        removeTaskOverlay(id);
        removeTaskOverlay(`illustration-players-container-${id}`);
      }
    });
  };

  //#region all task set position here
  const populateTasks = () => {
    const worldTask = getMapTasks();
    if (worldTask) {
      removeUnusedTaskOverlay(worldTask.map((task) => task.id));
      worldTask.forEach((worldTask) => {
        if (gameDocument.gameDocument && worldTask.boundary) {
          const task = gameDocument.gameDocument.assets?.tasks?.find((x) => {
            return x.boundary?.id === worldTask?.boundary?.id;
          });
          const findStatusTask = player.playerState?.tasks?.find((t) => {
            return t.id === worldTask.id;
          });
          const imgUrl = getTaskImage(
            gameDocument.gameDocument!,
            findStatusTask?.status!,
            worldTask,
            player?.playerState?.language?.name
          );
          const title = GetResourceValue(
            gameDocument.gameDocument,
            worldTask?.titleResId || '',
            player?.playerState?.language.name ?? '0'
          );

          const isVisible = isVisibleMapTask(task!, findStatusTask!);
          addTaskOverlay(
            worldTask.id,
            title,
            imgUrl,
            toLonLat(worldTask.boundary.geometry.coordinates),
            isFacilitator,
            isFacilitator ? true : isVisible
          );
        }
      });
    }
  };

  const onCompleteEventTaskAsync = async (task: TaskEntity) => {
    let updatedPlayer = cloneDeep(player);
    if (!updatedPlayer.playerState) return;

    const playerTask = {
      id: task.id,
      isVisible: true,
      status: 'completed' as 'completed'
    };

    const taskExist = updatedPlayer.playerState.tasks?.find(
      (t) => t.id === task.id
    );
    if (!taskExist) updatedPlayer.playerState.tasks?.push(playerTask);

    // hide task if hideWhenComplete true
    if (task.hideWhenComplete) {
      removeTaskOverlay(task.id);
    } else {
      changeTaskIconToComplete(task.id);
    }

    updatedPlayer.playerState!.latestCompletedTaskId = task.id;

    updatedPlayer = await executeTaskEvent(
      task.id,
      ['onOpenTask', 'onCompleteTask', 'onCloseTask'],
      updatedPlayer
    );

    setPlayer((prev) =>
      UpdatePlayerStateAsync(prev, updatedPlayer.playerState!)
    );
  };

  const openTaskContent = React.useCallback(
    async (id: string, isOnClick?: boolean, isAccesible: boolean = true) => {
      const proximitySettings = getProximitySettings(id);
      if (
        proximitySettings.tasksRequireProximity &&
        !isAccesible &&
        !proximitySettings.enablePremessageOutsideRange
      )
        return;

      const task = gameDocument.gameDocument?.assets?.tasks?.find(
        (x) => x.id === id
      );
      if (task) {
        if (task.taskContentId && task.taskContentId !== '') {
          //Get taskContent from gameDocument

          const { premessage } = isContainPremessageOrIntro(
            gameDocument.gameDocument!,
            task.taskContentId,
            player?.playerState?.language?.name!
          );

          const handlePlayerUpdate = () => {
            setPlayer((prev) => {
              const response = OpenTask(prev, task.id);
              return UpdatePlayerStateAsync(prev, response);
            });
          };

          if (proximitySettings.tasksRequireProximity) {
            if ((!isAccesible && premessage && isOnClick) || isAccesible) {
              handlePlayerUpdate();
              return;
            }
          } else {
            handlePlayerUpdate();
          }
        } else {
          // if task doesnt has task content, it will auto complete when open, then execute onOpen, onComplete, and onClose event
          await onCompleteEventTaskAsync(task);
        }
      }
    },
    [
      player,
      gameDocument.gameDocument?.assets?.tasks,
      setPlayer,
      getProximitySettings
    ]
  );

  const handleClickTask = React.useCallback(
    (e: Event) => {
      const gameDoc = gameDocument.gameDocument;
      if (gameDoc && (map || mapIllustration) && !isFacilitator) {
        const overlayElement = e.target as HTMLElement;
        let taskContainer = overlayElement;
        while (
          taskContainer.parentElement &&
          !taskContainer.id.startsWith('taskContainer-')
        ) {
          taskContainer = taskContainer.parentElement;
        }

        const taskId = taskContainer.id.replace('taskContainer-', '');
        if (!taskId) return;

        let taskPosition = mapContext.getOverlayById(taskId)?.getPosition() || [
          0, 0
        ];
        if (taskPosition) {
          taskPosition = toLonLat(taskPosition);
        }

        const proximitySettings = getProximitySettings(taskId);

        const isAccesible = checkIfTaskAccesible(taskPosition, taskId);
        const task = GetTaskById(player, taskId);
        const taskStatus = task && task.status && task.status === 'completed';
        if (
          proximitySettings.tasksRequireProximity !== undefined &&
          proximitySettings.tasksRequireProximity &&
          !isAccesible &&
          !taskStatus
        ) {
          addTaskDistance(taskId, taskPosition || [0, 0]);
        } else {
          removeTaskDistance(taskId);
        }

        const isTouchDevice =
          'ontouchstart' in window || navigator.maxTouchPoints > 0;

        if (isTouchDevice) {
          // For touch devices, toggle expanded state first
          if (!taskContainer.classList.contains('expanded')) {
            // move selected task to top of the overlay container
            moveTaskToTop(taskContainer);

            // let it move to top first, then expand and the animation still run smoothly
            setTimeout(() => {
              taskContainer.classList.add('expanded');
              taskContainer.classList.remove('initial');
              const detailTaskContainer = taskContainer.querySelector(
                '[id^="detailTaskContainer-"]'
              );
              detailTaskContainer?.classList.add('visible');
              detailTaskContainer?.classList.remove('hidden');
            }, 10);

            return;
          }

          // If already expanded, open the task
          openTaskContent(taskId, true, isAccesible);
        } else {
          // For non-touch devices, open immediately if expanded
          if (taskContainer.classList.contains('expanded')) {
            openTaskContent(taskId, true, isAccesible);
          }
        }
      }
    },
    [
      player,
      mapContext,
      isFacilitator,
      gameDocument.gameDocument,
      addTaskDistance,
      openTaskContent,
      removeTaskDistance,
      checkIfTaskAccesible
    ]
  );

  const handleCheckTaskProximity = (tasks: TaskEntity[]) => {
    tasks.forEach(async (task) => {
      let taskPosition = task.boundary?.geometry?.coordinates;

      if (taskPosition) {
        taskPosition = toLonLat(taskPosition);
        const isAccesible = checkIfTaskAccesible(taskPosition, task.id);

        // if player entered proximity radius, task will auto running task events
        if (isAccesible) {
          const updatedPlayer = await executeTaskEvent(task.id, [
            'onProximity'
          ]);
          setPlayer(updatedPlayer);
        }
      }
    });
  };

  const generateTaskProximity = React.useCallback(() => {
    if (mapType === 'illustrated') return;

    if (
      player.playerState?.coordinates?.longitude !== undefined &&
      player.playerState?.coordinates?.latitude !== undefined &&
      gameDocument.gameDocument &&
      inGameSetting
    ) {
      const { setTaskProximity } = inGameSetting;

      if (player.playerState.location === 'world-map') {
        const taskIdsOnMap = gameDocument.gameDocument.rules.worldMap.tasks.map(
          (i) => i.taskAssId
        );
        const tasks = gameDocument.gameDocument.assets.tasks
          ?.filter((i) => taskIdsOnMap.some((taskId) => taskId === i.id))
          .filter((i) => i.events?.onProximity);

        if (tasks && tasks?.length) {
          handleCheckTaskProximity(tasks);
        }
      } else if (
        player.playerState.location &&
        player.playerState.location !== 'world-map'
      ) {
        const zoneId = gameDocument.gameDocument.rules.worldMap.zones.map(
          (i) => i.zoneAssId
        );
        const zones = gameDocument.gameDocument.assets.zones?.filter((i) =>
          zoneId.some((zoneId) => zoneId === i.id)
        );
        const selectedZone = zones?.find(
          (i) => i.id === player.playerState?.location
        );
        const taskIdsOnZone = selectedZone?.tasks?.map((i) => i.taskAssId);
        if (taskIdsOnZone) {
          const tasks = gameDocument.gameDocument.assets.tasks
            ?.filter((i) => taskIdsOnZone.some((taskId) => taskId === i.id))
            .filter((i) => i.events?.onProximity);

          if (tasks && tasks?.length) {
            if (tasks && tasks?.length) {
              handleCheckTaskProximity(tasks);
            }
          }
        }
      }
    }
  }, [
    mapType,
    gameDocument.gameDocument,
    inGameSetting?.setTaskProximity,
    player.playerState?.coordinates?.latitude,
    player.playerState?.coordinates?.longitude
  ]);

  React.useEffect(() => {
    callbackCLickRef.current = handleClickTask;
  }, [handleClickTask]);

  React.useEffect(() => {
    if (mapContext) {
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;

      const overlayContainer = mapContext.getViewport();
      if (overlayContainer) {
        const handleEvent = (e: Event) => {
          if (callbackCLickRef.current) {
            callbackCLickRef.current(e);
          }
        };

        overlayContainer.addEventListener(
          isTouchDevice ? 'touchend' : 'click',
          handleEvent
        );

        return () => {
          overlayContainer.removeEventListener(
            isTouchDevice ? 'touchend' : 'click',
            handleEvent
          );
        };
      }
    }
  }, [mapContext]);

  // task detector for updating task distance and line based on player movement
  React.useEffect(() => {
    if (
      player.playerState?.coordinates?.longitude !== undefined &&
      player.playerState?.coordinates?.latitude !== undefined &&
      inGameSetting
    ) {
      const coordinates = [
        player.playerState?.coordinates?.longitude,
        player.playerState?.coordinates?.latitude
      ];
      const { setTaskProximity } = inGameSetting;

      const tasks = document.querySelectorAll('.task-container');
      tasks.forEach((task) => {
        const child = task.querySelector('[id^="taskDistance-"]');
        if (child && !child.classList.contains('d-none')) {
          const taskId = child.id.replace('taskDistance-', '');
          let taskPosition = mapContext
            .getOverlayById(taskId)
            ?.getPosition() || [0, 0];
          if (taskPosition) {
            taskPosition = toLonLat(taskPosition);
          }

          // update distance text in task
          const distances = getDistance(taskPosition, coordinates);
          updateTaskOverlay({ id: taskId, distance: Math.round(distances) });

          // update line following on player position
          const lineFeature = getLineDistance();
          if (lineFeature) {
            const linePosition = lineFeature.getGeometry()?.getCoordinates();
            if (linePosition) {
              lineFeature
                .getGeometry()
                ?.setCoordinates([linePosition[0], fromLonLat(coordinates)]);
            }
          }

          const isAccesible = checkIfTaskAccesible(taskPosition, taskId);

          // if player entered proximity radius, task will auto open
          openTaskContent(taskId, false, isAccesible);
        }
      });
    }
  }, [
    mapContext,
    inGameSetting,
    player.playerState?.coordinates?.latitude,
    player.playerState?.coordinates?.longitude
  ]);

  React.useEffect(() => {
    generateTaskProximity();
  }, [
    player.playerState?.coordinates?.latitude,
    player.playerState?.coordinates?.longitude
  ]);

  return {
    getMapTasks,
    openTaskContent,
    updateTaskOverlay,
    removeTaskDistance,
    checkIfTaskAccesible,
    populateTaskOverlay: populateTasks,
    changeTaskIconToComplete,
    changeTaskIconToInComplete,
    isTaskAccessible
  };
};
